import type { FlowEdge, FlowNode } from '../types/flow-types';
import { resolveDeviceInStore } from './resolveDeviceInStore';

export type FlowRepairKind =
	| 'runtime_orphan'
	| 'missing_device'
	| 'dangling_edge'
	| 'switch_incomplete'
	| 'invalid_timer_mode'
	| 'configuration';

export interface FlowRepairSuggestion {
	id: 'reconfigure' | 'remove-node' | 'remove-edge';
	label: string;
	destructive?: boolean;
}

export interface FlowRepairIssue {
	id: string;
	kind: FlowRepairKind;
	message: string;
	nodeId?: string;
	edgeId?: string;
	suggestions: FlowRepairSuggestion[];
}

function configuredDeviceId(node: FlowNode): string {
	return String(
		node.data?.device?.id ||
			(node.data?._device_condition as any)?.device?.id ||
			(node.data?.sceneCapability as any)?.deviceId ||
			node.data?.deviceRealId ||
			node.data?.deviceId ||
			'',
	);
}

export function analyzeFlowRepairIssues(
	nodes: FlowNode[],
	edges: FlowEdge[],
	devices: Record<string, unknown> = {},
): FlowRepairIssue[] {
	const issues: FlowRepairIssue[] = [];
	const nodeIds = new Set(nodes.map((node) => String(node.id)));
	for (const edge of edges) {
		if (nodeIds.has(String(edge.source)) && nodeIds.has(String(edge.target))) continue;
		issues.push({
			id: `dangling-edge:${edge.id}`,
			kind: 'dangling_edge',
			edgeId: edge.id,
			message: 'Connection points to a node that no longer exists',
			suggestions: [{ id: 'remove-edge', label: 'Remove connection', destructive: true }],
		});
	}

	for (const node of nodes) {
		for (const message of node.errors || []) {
			const kind: FlowRepairKind = message.includes('runtime')
				? 'runtime_orphan'
				: message.includes('WAIT FOR')
					? 'invalid_timer_mode'
					: message.includes('Switch')
						? 'switch_incomplete'
						: 'configuration';
			issues.push({
				id: `${kind}:${node.id}:${issues.length}`,
				kind,
				nodeId: node.id,
				message,
				suggestions: [
					{ id: 'reconfigure', label: 'Open and reconfigure' },
					{ id: 'remove-node', label: 'Remove node', destructive: true },
				],
			});
		}
	}

	for (const node of nodes) {
		const deviceId = configuredDeviceId(node);
		const channel =
			parseInt(String(node.data?.channel ?? node.data?.device?.channel ?? '0'), 10) || 0;
		if (!deviceId || resolveDeviceInStore(devices, deviceId, channel)) continue;
		issues.push({
			id: `missing-device:${node.id}`,
			kind: 'missing_device',
			nodeId: node.id,
			message: `Device used by "${node.data?.label || node.id}" is not available in this account`,
			suggestions: [
				{ id: 'reconfigure', label: 'Choose replacement device' },
				{ id: 'remove-node', label: 'Remove node', destructive: true },
			],
		});
	}

	const seen = new Set<string>();
	return issues.filter((issue) => {
		const key = `${issue.kind}:${issue.nodeId || issue.edgeId || ''}:${issue.message}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

export function applyFlowRepair(
	nodes: FlowNode[],
	edges: FlowEdge[],
	issue: FlowRepairIssue,
	suggestionId: FlowRepairSuggestion['id'],
): { nodes: FlowNode[]; edges: FlowEdge[] } {
	if (suggestionId === 'remove-edge' && issue.edgeId) {
		return { nodes, edges: edges.filter((edge) => edge.id !== issue.edgeId) };
	}
	if (suggestionId === 'remove-node' && issue.nodeId) {
		return {
			nodes: nodes.filter((node) => node.id !== issue.nodeId),
			edges: edges.filter((edge) => edge.source !== issue.nodeId && edge.target !== issue.nodeId),
		};
	}
	return { nodes, edges };
}
